/**
 * SPA entry — one bundle, two roles, decided by the route (mirrors spot-client's
 * `src/index.tsx` + `app.tsx` `<Switch>`):
 *
 *   TV     routes: /tv, /setup, /meeting     -> TvApp
 *   Share  routes: /share                    -> ShareApp (laptop wireless screenshare)
 *   Remote routes: /, /remote-control, …      -> RemoteApp (the catch-all)
 *
 * Config is read from window.__SPACES_CONFIG__ (served by the Worker). If it is
 * missing/invalid we fail loudly on-screen rather than silently defaulting.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { getConfig } from './config.js';
import { log } from './log.js';
import { RemoteApp } from './remote/RemoteApp.js';
import { ShareApp } from './share/ShareApp.js';
import { TvApp } from './tv/TvApp.js';
import './styles.css';

const TV_ROUTES = new Set(['/tv', '/setup', '/meeting']);

function normalizePath(pathname: string): string {
    return pathname.replace(/\/+$/, '') || '/';
}

function isTvRoute(pathname: string): boolean {
    return TV_ROUTES.has(normalizePath(pathname));
}

function isShareRoute(pathname: string): boolean {
    return normalizePath(pathname) === '/share';
}

function Root(): JSX.Element {
    const path = window.location.pathname;

    if (isTvRoute(path)) {
        return <TvApp />;
    }
    if (isShareRoute(path)) {
        return <ShareApp />;
    }

    return <RemoteApp />;
}

function FatalConfigError({ message }: { message: string }): JSX.Element {
    return (
        <div className = "fatal">
            <div className = "fatal__card">
                <h1>Spaces client misconfigured</h1>
                <p>{ message }</p>
            </div>
        </div>
    );
}

const container = document.getElementById('spaces-root');

if (!container) {
    throw new Error('#spaces-root not found');
}

const root = createRoot(container);

try {
    // Validate config eagerly so a config failure renders a clear error, not a blank page.
    getConfig();
    root.render(<StrictMode><Root /></StrictMode>);
    log.log('Spaces client booted', { role: isTvRoute(window.location.pathname) ? 'tv' : 'remote' });
} catch (err) {
    log.error('boot failed', { error: String(err) });
    root.render(<FatalConfigError message = { (err as Error).message } />);
}
